/**
 * ActivityEntryStep — multi-add activities entry table (Step 2 of the
 * `log-activity` flow, GU-7008 redesign).
 *
 * Shape mirrors the asset-detail page: a header bar with toolbar buttons
 * (Add Activity / Bulk Add Activities / OCR) + an `ActivityTable` listing
 * staged rows. Everything in this step is in-memory client state — nothing
 * hits the DB until the user clicks Submit, which fires per-group
 * `POST /imports` calls (one per bulk group with its source file, one for
 * manual rows without a file). On success, the parent advances to Step 3.
 *
 * On submit failure the partial state is rolled back: every BulkImport
 * created in this submission attempt is undone so the user lands back on
 * Step 2 with their staged rows intact.
 */

import { useCallback, useMemo, useState } from 'react';
import { Plus, Upload, FileSpreadsheet, X as XIcon, PencilLine, RefreshCw, FileText } from 'lucide-react';
import { Button as ReactstrapButton } from 'reactstrap';
import { ConfirmationModal } from '@g17eco/molecules/confirm-modal';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { BlockingLoader } from '@g17eco/atoms/loader';
import {
  useCreateBulkImportMutation,
  useUndoImportMutation,
} from '../../../api/emissions';
import {
  EmissionScope,
  type AlternativeFactor,
  type CreateActivityRequest,
} from '../../../types/emissions';
import { expandMonthToPeriodRange } from '../../../utils/activityUtils';
import {
  type AssetActivityEntry,
  type BulkGroup,
  type StagedRow,
  createStagedRow,
} from '../types';
import { INTENT_PROVIDER_SECTIONS } from '../requestAccessConfig';
import { useFactorPreview } from '../../../hooks/useFactorPreview';
import { AddActivityModal } from './AddActivityModal';
import { BulkImportModal, type BulkImportAccept } from './BulkImportModal';
import { ConnectProviderModal } from './ConnectProviderModal';
import { ScanInvoiceModal, type ScanInvoiceAccept } from './ScanInvoiceModal';
import { StagingTable } from '../StagingTable';
import { type UseUploadSessionResult } from '../../../hooks/useUploadSession';

const SOURCE_TOOLTIP_MANUAL = 'Manually added';

const newGroupId = () => crypto.randomUUID();

/**
 * Maps a StagedRow to a CreateActivityRequest for the `POST /imports`
 * payload. Only sends `climatiqFactorId` when the user actually picked an
 * alternative — otherwise the server runs its own factor search so the
 * calculation isn't flagged as a user override.
 */
const toCreateRequest = (row: StagedRow): CreateActivityRequest => {
  const r = row.request;
  return {
    scope: r.scope,
    activityType: r.activityType,
    ...expandMonthToPeriodRange(r.periodStart, r.periodEnd),
    quantity: Number(r.quantity) || 0,
    unit: r.unit as CreateActivityRequest['unit'],
    fuelType: r.fuelType || undefined,
    vehicleType: r.vehicleType || undefined,
    refrigerantType: r.refrigerantType ? (r.refrigerantType as CreateActivityRequest['refrigerantType']) : undefined,
    processType: r.processType || undefined,
    region: r.region,
    regionFallback: r.regionFallback,
    yearFallback: r.yearFallback,
    dataSource: r.dataSource || undefined,
    notes: r.notes || undefined,
    name: r.name || undefined,
    ...(row.overrideFactorId ? { climatiqFactorId: row.overrideFactorId } : {}),
  };
};

export interface ActivityEntryStepProps {
  initiativeId: string;
  /**
   * Staged rows are owned by the parent so they survive Review-step
   * cancel (which re-mounts this component).
   */
  rows: StagedRow[];
  setRows: (updater: (prev: StagedRow[]) => StagedRow[]) => void;
  bulkGroups: BulkGroup[];
  setBulkGroups: (updater: (prev: BulkGroup[]) => BulkGroup[]) => void;
  /** Called once submit succeeds with the list of newly created BulkImport ids. */
  onSubmitSuccess: (importIds: string[]) => void;
  /**
   * Organisation default region (from `EmissionSettings.defaultRegion`),
   * pre-filled into every newly-added activity unless the user changes
   * it. Empty string when no default is set.
   */
  defaultRegion?: string;
  /**
   * Shared upload session from `ActivityFormFlow`. Required for the
   * Scan Invoice modal (GU-6981) so the uploaded invoices end up on
   * the same session the post-submit Evidence step reads. When unset
   * (legacy callers / tests that don't exercise scan-invoice), the
   * Scan Invoice toolbar button is hidden.
   */
  uploadSession?: UseUploadSessionResult;
  /**
   * Open the Scan Invoice modal automatically on first mount. Used when
   * the user picks the Scan Invoice method from outside the entry step
   * (intent picker, stub-entry step) — we route them straight into the
   * entry table with the modal already open so they don't see an empty
   * page first.
   */
  autoOpenScanInvoiceModal?: boolean;
}

export const ActivityEntryStep = ({
  initiativeId,
  rows,
  setRows,
  bulkGroups,
  setBulkGroups,
  onSubmitSuccess,
  defaultRegion = '',
  uploadSession,
  autoOpenScanInvoiceModal = false,
}: ActivityEntryStepProps) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [scanInvoiceModalOpen, setScanInvoiceModalOpen] = useState(autoOpenScanInvoiceModal);
  const utilityProviderSection = INTENT_PROVIDER_SECTIONS.activity;
  // When set, the AddActivityModal opens in edit mode prefilled with this row.
  const [editingRow, setEditingRow] = useState<StagedRow | null>(null);

  const [confirmRemoveGroup, setConfirmRemoveGroup] = useState<BulkGroup | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createBulkImport] = useCreateBulkImportMutation();
  const [undoImport] = useUndoImportMutation();

  // Factor-preview lookup is shared with the asset wizard via `useFactorPreview`.
  const { buildSearchQuery, retrigger } = useFactorPreview(rows, initiativeId, setRows);

  // Kebab "Re-run factor search" — clear the row's resolved/override
  // fields and the hook's cached key so the next render re-fires.
  const handleRetriggerFactorSearch = useCallback(
    (rowId: string) => {
      retrigger(rowId);
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                resolvedFactorId: undefined,
                resolvedFactorName: undefined,
                resolvedFactorValue: undefined,
                resolvedFactorUnit: undefined,
                factorAlternatives: undefined,
                resolveAttempted: false,
                overrideFactorId: undefined,
                overrideFactorName: undefined,
                overrideFactorValue: undefined,
                overrideFactorUnit: undefined,
              }
            : r,
        ),
      );
    },
    [retrigger, setRows],
  );

  // Map for fast group lookup when rendering the table.
  const groupById = useMemo(() => {
    const map = new Map<string, BulkGroup>();
    for (const group of bulkGroups) {
      map.set(group.id, group);
    }
    return map;
  }, [bulkGroups]);

  // ─── Manual add ──────────────────────────────────────────────────────────

  const handleAddSave = useCallback((entry: AssetActivityEntry) => {
    if (editingRow) {
      // Edit path: replace the row's request, keep id + bulkGroupId so
      // edited bulk-imported rows stay tied to their original group.
      // Drop any cached resolved/override factor — classification may have
      // changed, so the next render re-resolves via the search effect.
      setRows((prev) =>
        prev.map((r) =>
          r.id === editingRow.id
            ? {
                ...r,
                request: entry,
                resolvedFactorId: undefined,
                resolvedFactorName: undefined,
                resolvedFactorValue: undefined,
                resolvedFactorUnit: undefined,
                overrideFactorId: undefined,
                overrideFactorName: undefined,
                overrideFactorValue: undefined,
                overrideFactorUnit: undefined,
                factorAlternatives: undefined,
              }
            : r,
        ),
      );
      setEditingRow(null);
      return;
    }
    setRows((prev) => [...prev, createStagedRow(entry)]);
  }, [setRows, editingRow]);

  const handleEditRow = useCallback(
    (rowId: string) => {
      const row = rows.find((r) => r.id === rowId);
      if (!row) {
        return;
      }
      setEditingRow(row);
      setAddModalOpen(true);
    },
    [rows],
  );

  // ─── Bulk import ─────────────────────────────────────────────────────────

  const handleBulkAccept = useCallback((result: BulkImportAccept) => {
    const groupId = newGroupId();
    const group: BulkGroup = {
      id: groupId,
      file: result.file,
      filename: result.file.name,
      scope: result.scope,
      activityType: result.activityType,
    };
    setBulkGroups((prev) => [...prev, group]);

    const newRows = result.activities.map((req) => {
      // Convert CreateActivityRequest → AssetActivityEntry (the modal-shape)
      // for symmetry with manual rows. Both end up in the same StagedRow
      // shape and the same toCreateRequest path on submit.
      const entry: AssetActivityEntry = {
        sourceLabel: 'Bulk import',
        scope: req.scope,
        activityType: req.activityType,
        quantity: String(req.quantity),
        unit: req.unit,
        fuelType: req.fuelType ?? '',
        vehicleType: req.vehicleType ?? '',
        refrigerantType: (req.refrigerantType ?? req.gasType ?? '') as AssetActivityEntry['refrigerantType'],
        processType: req.processType ?? '',
        region: req.region,
        regionFallback: req.regionFallback ?? true,
        yearFallback: req.yearFallback ?? true,
        periodStart: req.periodStart,
        periodEnd: req.periodEnd,
        name: req.name ?? '',
        dataSource: req.dataSource ?? '',
        notes: req.notes ?? '',
      };
      return createStagedRow(entry, groupId);
    });
    setRows((prev) => [...prev, ...newRows]);
    setBulkModalOpen(false);
  }, [setRows, setBulkGroups]);

  // GU-6981 — ScanInvoiceModal returns rows shaped exactly like manual rows
  // (no BulkGroup, no source file). The invoices are already staged in the
  // shared `uploadSession` and will appear pre-attachable on the post-submit
  // Evidence step. Rows are tagged with `sourceLabel: 'Scan invoice'` so
  // the StagingTable surfaces provenance.
  const handleScanInvoiceAccept = useCallback(
    (result: ScanInvoiceAccept) => {
      const newRows = result.activities.map((req) => {
        const entry: AssetActivityEntry = {
          sourceLabel: 'Scan invoice',
          scope: req.scope,
          activityType: req.activityType,
          quantity: String(req.quantity),
          unit: req.unit,
          fuelType: req.fuelType ?? '',
          vehicleType: req.vehicleType ?? '',
          refrigerantType: (req.refrigerantType ?? req.gasType ?? '') as AssetActivityEntry['refrigerantType'],
          processType: req.processType ?? '',
          region: req.region,
          regionFallback: req.regionFallback ?? true,
          yearFallback: req.yearFallback ?? true,
          periodStart: req.periodStart,
          periodEnd: req.periodEnd,
          name: req.name ?? '',
          dataSource: req.dataSource ?? '',
          notes: req.notes ?? '',
        };
        return createStagedRow(entry);
      });
      setRows((prev) => [...prev, ...newRows]);
      setScanInvoiceModalOpen(false);
    },
    [setRows],
  );

  // ─── Row management ──────────────────────────────────────────────────────

  const handleDeleteRow = useCallback((rowId: string) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== rowId);
      // If a bulk group has zero rows left after this delete, drop the group too.
      const remainingGroupIds = new Set(next.map((r) => r.bulkGroupId).filter(Boolean) as string[]);
      setBulkGroups((groups) => groups.filter((g) => remainingGroupIds.has(g.id)));
      return next;
    });
  }, [setRows, setBulkGroups]);

  const handleRemoveBulkGroup = useCallback((groupId: string) => {
    setRows((prev) => prev.filter((r) => r.bulkGroupId !== groupId));
    setBulkGroups((groups) => groups.filter((g) => g.id !== groupId));
    setConfirmRemoveGroup(null);
  }, [setRows, setBulkGroups]);

  // ─── Factor swap ─────────────────────────────────────────────────────────

  const handlePickAlternative = useCallback(
    (rowId: string, factor: AlternativeFactor) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== rowId) {
            return r;
          }
          // Picking the same factor as the auto-resolved one is a no-op
          // override — clear instead so the row reverts cleanly.
          if (factor.id === r.resolvedFactorId) {
            return {
              ...r,
              overrideFactorId: undefined,
              overrideFactorName: undefined,
              overrideFactorValue: undefined,
              overrideFactorUnit: undefined,
            };
          }
          return {
            ...r,
            overrideFactorId: factor.id,
            overrideFactorName: factor.name,
            overrideFactorValue: factor.factor ?? undefined,
            overrideFactorUnit: factor.unit,
          };
        }),
      );
    },
    [setRows],
  );

  const handleRevertOverride = useCallback(
    (rowId: string) => {
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                overrideFactorId: undefined,
                overrideFactorName: undefined,
                overrideFactorValue: undefined,
                overrideFactorUnit: undefined,
              }
            : r,
        ),
      );
    },
    [setRows],
  );

  // ─── Submit ──────────────────────────────────────────────────────────────

  const submittablesByGroup = useMemo(() => {
    // Returns an array of submission groups: each carries the activities to
    // post + (optionally) the source file. Manual rows are bundled into a
    // single virtual group with no file.
    const byGroup = new Map<string | null, StagedRow[]>();
    for (const row of rows) {
      const key = row.bulkGroupId ?? null;
      const list = byGroup.get(key) ?? [];
      list.push(row);
      byGroup.set(key, list);
    }
    return Array.from(byGroup.entries()).map(([groupId, groupRows]) => ({
      groupId,
      group: groupId ? groupById.get(groupId) : undefined,
      rows: groupRows,
    }));
  }, [rows, groupById]);

  const handleSubmit = useCallback(async () => {
    if (rows.length === 0 || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const created: string[] = [];
    try {
      for (const submitGroup of submittablesByGroup) {
        const groupRows = submitGroup.rows;
        if (groupRows.length === 0) {
          continue;
        }
        // Determine scope for this submission. Bulk groups have a stamped
        // scope; manual groups derive scope from their first row (server
        // requires a single scope per import — since manual rows can mix
        // scopes, we'd need to split the manual group by scope here too.
        // Slice 1: split by scope as well.
        const byScope = new Map<EmissionScope, StagedRow[]>();
        for (const r of groupRows) {
          if (!r.request.scope) {
            continue;
          }
          const list = byScope.get(r.request.scope) ?? [];
          list.push(r);
          byScope.set(r.request.scope, list);
        }
        for (const [scope, scopeRows] of byScope.entries()) {
          const activities = scopeRows.map(toCreateRequest);
          const result = await createBulkImport({
            initiativeId,
            ...(submitGroup.group ? { file: submitGroup.group.file } : {}),
            activities: JSON.stringify(activities),
            scope,
            ...(submitGroup.group?.activityType ? { activityType: submitGroup.group.activityType } : {}),
            regionFallback: true,
            yearFallback: true,
          });
          if ('error' in result) {
            throw new Error('Bulk import request failed');
          }
          created.push(result.data._id);
        }
      }
      onSubmitSuccess(created);
    } catch (err) {
      // Atomic rollback: undo every BulkImport created in this attempt so
      // the user lands back on Step 2 with their staged rows intact.
      for (const importId of created) {
        await undoImport({ initiativeId, importId }).catch(() => {});
      }
      toastFromError(err);
      generateToast({
        color: 'danger',
        message: 'Could not submit your activities. Your staged rows are still here — please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [rows.length, isSubmitting, submittablesByGroup, createBulkImport, initiativeId, onSubmitSuccess, undoImport]);

  // ─── Render ──────────────────────────────────────────────────────────────

  const totalRows = rows.length;
  const manualCount = rows.filter((r) => !r.bulkGroupId).length;

  const emptyState = (
    <div className='tw:border-dashed tw:border-2 tw:border-border'>
      <div className='tw:p-12 tw:text-center'>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-4 tw:gap-4'>
          No activities staged yet. Use the toolbar buttons above to start adding records.
        </p>
      </div>
    </div>
  );

  return (
    <>
      {/* Block all interaction during submit — prevents accidental edits/
          clicks while the per-group POST /imports calls are in flight. */}
      {isSubmitting && <BlockingLoader />}
      <Card className='tw:mb-6'>
        <CardContent className='tw:p-6'>
          <div className='tw:flex tw:items-start tw:justify-between tw:mb-4 tw:gap-4'>
            <div>
              <h5 className='tw:font-semibold tw:mb-1'>
                Activities ({totalRows})
              </h5>
              <div className='tw:text-sm tw:text-muted-foreground'>
                Add activities one at a time, in bulk via XLSX, or by scanning an invoice. Nothing is saved until you click Submit.
              </div>
            </div>
            <div className='tw:flex tw:gap-2 tw:items-center tw:flex-shrink-0'>
              <Button
                size='sm' 
                variant='default'
                onClick={() => setAddModalOpen(true)}
              >
                <Plus className='tw:size-4' />
                Add
              </Button>
              <Button size='sm' variant='outline' onClick={() => setBulkModalOpen(true)}>
                <Upload className='tw:size-4' />
                Upload XLSX
              </Button>
              {uploadSession && (
                <Button size='sm' variant='outline' onClick={() => setScanInvoiceModalOpen(true)}>
                  <FileText className='tw:size-4' />
                  Upload Invoice
                </Button>
              )}
              {utilityProviderSection && (
                <Button size='sm' variant='outline' onClick={() => setConnectModalOpen(true)}>
                  <RefreshCw className='tw:size-4' />
                  Connect
                </Button>
              )}
            </div>
          </div>

          {bulkGroups.length > 0 && (
            <div className='tw:mb-4 tw:flex tw:flex-wrap tw:gap-2 tw:items-center'>
              <span className='tw:text-xs tw:font-medium tw:text-muted-foreground tw:uppercase tw:tracking-wider'>
                Uploads
              </span>
              {bulkGroups.map((group) => {
                const count = rows.filter((r) => r.bulkGroupId === group.id).length;
                return (
                  <Badge
                    key={group.id}
                    variant='emerald-light'
                    className={cn(
                      'tw:flex tw:items-center tw:gap-1.5 tw:pe-0 tw:font-normal',
                    )}
                  >
                    <FileSpreadsheet className='tw:size-3' />
                    <span>{group.filename}</span>
                    <span className='tw:text-muted-foreground'>· {count}</span>
                    <Button
                      variant='emerald'
                      size='icon-xs'
                      className='tw:ml-2'
                      onClick={() => setConfirmRemoveGroup(group)}
                      aria-label={`Remove all rows from ${group.filename}`}
                    >
                      <XIcon />
                    </Button>
                  </Badge>
                );
              })}
              {manualCount > 0 && (
                <Badge variant='secondary' className='tw:flex tw:items-center tw:gap-1.5 tw:px-2 tw:py-1 tw:font-normal'>
                  <PencilLine className='tw:size-3' />
                  <span>{SOURCE_TOOLTIP_MANUAL}</span>
                  <span className='tw:text-muted-foreground'>· {manualCount}</span>
                </Badge>
              )}
            </div>
          )}

          {totalRows === 0 ? (
            emptyState
          ) : (
            <StagingTable
              rows={rows}
              groupById={groupById}
              initiativeId={initiativeId}
              onEdit={handleEditRow}
              onDelete={handleDeleteRow}
              onPickAlternative={handlePickAlternative}
              onRevertOverride={handleRevertOverride}
              onRetriggerFactorSearch={handleRetriggerFactorSearch}
            />
          )}

          <div className='tw:flex tw:justify-end tw:items-center tw:mt-4'>
            <Button
              onClick={handleSubmit}
              disabled={totalRows === 0 || isSubmitting || rows.some((r) => !r.resolvedFactorId && !r.resolveAttempted && buildSearchQuery(r) !== null)}
              className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0'
            >
              {isSubmitting ? 'Submitting…' : `Submit ${totalRows || ''} activities`.trim()}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AddActivityModal
        isOpen={addModalOpen}
        defaults={{ region: defaultRegion, periodStart: '', periodEnd: '' }}
        editing={
          editingRow
            ? {
                entry: editingRow.request,
                label: editingRow.request.sourceLabel ?? editingRow.request.name ?? 'Activity',
              }
            : null
        }
        onSave={handleAddSave}
        onClose={() => {
          setAddModalOpen(false);
          setEditingRow(null);
        }}
      />

      <BulkImportModal
        isOpen={bulkModalOpen}
        initiativeId={initiativeId}
        onAccept={handleBulkAccept}
        onClose={() => setBulkModalOpen(false)}
      />

      {uploadSession && (
        <ScanInvoiceModal
          isOpen={scanInvoiceModalOpen}
          initiativeId={initiativeId}
          uploadSession={uploadSession}
          onAccept={handleScanInvoiceAccept}
          onClose={() => setScanInvoiceModalOpen(false)}
        />
      )}

      {utilityProviderSection && (
        <ConnectProviderModal
          isOpen={connectModalOpen}
          section={utilityProviderSection}
          onClose={() => setConnectModalOpen(false)}
        />
      )}

      {confirmRemoveGroup && (
        <ConfirmationModal
          isOpen={true}
          title={`Remove rows from ${confirmRemoveGroup.filename}?`}
          content={
            <p className='tw:text-sm tw:mb-0'>
              This will remove all {rows.filter((r) => r.bulkGroupId === confirmRemoveGroup.id).length} staged rows
              from this file. The file itself isn't deleted — you can re-import it via Bulk Add.
            </p>
          }
          confirmButton={
            <ReactstrapButton color='danger' onClick={() => handleRemoveBulkGroup(confirmRemoveGroup.id)}>
              Remove all
            </ReactstrapButton>
          }
          handleCancel={() => setConfirmRemoveGroup(null)}
        />
      )}
    </>
  );
};
